import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type MembershipStatus = "pending" | "approved" | "rejected";

export interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  birth_date: string | null;
  instagram: string | null;
  city: string | null;
  photo_url: string | null;
  bio: string | null;
  membership_status: MembershipStatus;
  points_balance: number;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isAdmin: boolean;
  isSuperadmin: boolean;
  isWaiter: boolean;
  loading: boolean;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}


const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [isWaiter, setIsWaiter] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadProfileAndRole = async (userId: string) => {
    const [{ data: profileData }, { data: roleData }] = await Promise.all([
      supabase.from("profiles").select("*").eq("id", userId).maybeSingle(),
      supabase.from("user_roles").select("role").eq("user_id", userId),
    ]);
    setProfile((profileData as Profile | null) ?? null);
    const roles = (roleData ?? []).map((r: { role: string }) => r.role);
    setIsAdmin(roles.includes("admin") || roles.includes("superadmin"));
    setIsSuperadmin(roles.includes("superadmin"));
    setIsWaiter(roles.includes("waiter"));
  };


  useEffect(() => {
    let lastLoadedUserId: string | null = null;

    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);

      // Sadece kimlik değişimlerinde profil çek — TOKEN_REFRESHED / INITIAL_SESSION
      // sürekli yeniden fetch yaparak donmaya yol açıyordu.
      if (event === "SIGNED_OUT" || !newSession?.user) {
        lastLoadedUserId = null;
        setProfile(null);
        setIsAdmin(false);
        setIsSuperadmin(false);
        setIsWaiter(false);

        return;
      }

      if (event === "SIGNED_IN" || event === "USER_UPDATED") {
        const uid = newSession.user.id;
        if (uid === lastLoadedUserId) return;
        lastLoadedUserId = uid;
        setTimeout(() => {
          void loadProfileAndRole(uid);
        }, 0);
      }
    });

    void supabase.auth.getSession().then(({ data: { session: existing } }) => {
      setSession(existing);
      setUser(existing?.user ?? null);
      if (existing?.user) {
        lastLoadedUserId = existing.user.id;
        void loadProfileAndRole(existing.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  // Profil değişimleri için pencere fokuslandığında yeniden yükle.
  // (Realtime yayını güvenlik nedeniyle kapatıldı.)
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) return;
    const onFocus = () => {
      void loadProfileAndRole(userId);
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [userId]);

  const refreshProfile = async () => {
    if (user) await loadProfileAndRole(user.id);
  };

  const signOut = async () => {
    if (user) {
      try {
        await supabase.from("page_visits").insert({
          user_id: user.id,
          path: typeof window !== "undefined" ? window.location.pathname : "/",
          event_type: "logout",
        });
      } catch (e) {
        console.error("logout track failed", e);
      }
    }
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, isAdmin, isSuperadmin, isWaiter, loading, refreshProfile, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
